import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellRing, X } from 'lucide-react';
interface DailyRewriteReminderProps {
  onDismiss?: () => void;
}
export function DailyRewriteReminder({
  onDismiss
}: DailyRewriteReminderProps) {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    // Check if dismissed in this session
    const isDismissed = sessionStorage.getItem('rewrite_reminder_dismissed');
    if (!isDismissed) {
      // Small delay to let the page load first
      const timer = setTimeout(() => setIsVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);
  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem('rewrite_reminder_dismissed', 'true');
    if (onDismiss) onDismiss();
  };
  const handleScrollToInput = () => {
    const inputEl = document.getElementById('rewriter-input');
    if (inputEl) {
      inputEl.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      // Optional: focus the textarea after scrolling
      setTimeout(() => {
        const textarea = inputEl.querySelector('textarea');
        if (textarea) textarea.focus();
      }, 500);
    }
  };
  return <AnimatePresence data-id="element-2075">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: -20,
      height: 0
    }} animate={{
      opacity: 1,
      y: 0,
      height: 'auto'
    }} exit={{
      opacity: 0,
      y: -20,
      height: 0
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 25
    }} className="mb-8 overflow-hidden" data-id="element-2076">
          <div className="bg-gradient-to-r from-[#0a0a0a] to-[#111] border border-gray-800 rounded-xl p-4 relative group" data-id="element-2077">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600" data-id="element-2078" />
            <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600" data-id="element-2079" />
            <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600" data-id="element-2080" />
            <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600" data-id="element-2081" />

            <button onClick={handleDismiss} className="absolute top-3 right-3 p-1.5 text-gray-500 hover:text-white hover:bg-white/5 rounded-md transition-colors" aria-label="Dismiss reminder" data-id="element-2082">
              <X size={16} data-id="element-2083" />
            </button>

            <div className="flex items-start sm:items-center gap-4 pr-8" data-id="element-2084">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FF0000]/10 border border-[#FF0000]/20 flex items-center justify-center" data-id="element-2085">
                <BellRing className="w-5 h-5 text-[#FF0000]" data-id="element-2086" />
              </div>

              <div className="flex-1" data-id="element-2087">
                <h3 className="text-white font-bold text-sm mb-1 uppercase tracking-wide" data-id="element-2088">
                  Daily Rewrite Reminder
                </h3>
                <p className="text-gray-400 text-xs font-mono leading-relaxed mb-2 sm:mb-0" data-id="element-2089">
                  You have 3 free rewrites today. Practice healthier
                  communication — your words matter. 💬
                </p>
              </div>

              <div className="hidden sm:block flex-shrink-0" data-id="element-2090">
                <button onClick={handleScrollToInput} className="text-xs font-bold text-[#FF0000] hover:text-red-400 uppercase tracking-widest transition-colors" data-id="element-2091">
                  Start Rewriting →
                </button>
              </div>
            </div>

            {/* Mobile CTA */}
            <div className="sm:hidden mt-3 pt-3 border-t border-gray-800" data-id="element-2092">
              <button onClick={handleScrollToInput} className="text-xs font-bold text-[#FF0000] hover:text-red-400 uppercase tracking-widest transition-colors w-full text-left" data-id="element-2093">
                Start Rewriting →
              </button>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}